// src/pages/Dashboard.tsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  PlayCircle,
  CheckCircle,
  BookOpen,
  TrendingUp,
  Award,
  ChevronDown,
  ChevronUp,
  Clock,
  FileText,
  Play,
  GraduationCap,
} from "lucide-react";
import { useGetStudentCoursesQuery } from "../services/courses";
import { useAppSelector } from "../app/hooks";

/* ─── helpers ─────────────────────────────────────────────────── */

/** Extract a YouTube video ID from common URL patterns or bare IDs. */
function extractYoutubeId(value?: string): string | null {
  if (!value) return null;
  // already a bare id (11 chars, no slashes)
  if (/^[a-zA-Z0-9_-]{11}$/.test(value.trim())) return value.trim();
  const patterns = [
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([a-zA-Z0-9_-]{11})/,
    /(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/,
  ];
  for (const re of patterns) {
    const m = value.match(re);
    if (m) return m[1];
  }
  return null;
}

/** YouTube thumbnail — tries hqdefault, falls back to a teal placeholder. */
const YoutubeThumbnail: React.FC<{
  videoId: string | null;
  duration?: number;
  size?: "sm" | "lg";
}> = ({ videoId, duration, size = "lg" }) => {
  const [errored, setErrored] = useState(false);
  const sizeClass = size === "lg" ? "w-40 h-24" : "w-28 h-18";

  const fmtDuration = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = String(secs % 60).padStart(2, "0");
    return `${m}:${s}`;
  };

  return (
    <div
      className={`${sizeClass} relative flex-shrink-0 rounded-xl overflow-hidden bg-gradient-to-br from-teal-400 to-teal-600 group-hover:brightness-90 transition-all`}
    >
      {videoId && !errored ? (
        <img
          src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`}
          alt="Video thumbnail"
          className="w-full h-full object-cover"
          onError={() => setErrored(true)}
        />
      ) : (
        /* fallback gradient */
        <div className="absolute inset-0 flex items-center justify-center">
          <PlayCircle size={size === "lg" ? 44 : 28} className="text-white opacity-80" />
        </div>
      )}

      {/* dark overlay + centered play */}
      <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
          <Play size={16} className="text-teal-600 ml-0.5" />
        </div>
      </div>

      {/* always-visible play badge (small) */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none group-hover:opacity-0 transition-opacity">
        <div className="w-8 h-8 rounded-full bg-black/50 flex items-center justify-center">
          <Play size={12} className="text-white ml-0.5" />
        </div>
      </div>

      {duration ? (
        <div className="absolute bottom-1.5 right-1.5 bg-black/70 px-1.5 py-0.5 rounded text-[10px] text-white font-semibold">
          {fmtDuration(duration)}
        </div>
      ) : null}
    </div>
  );
};

/* ─── stat card ───────────────────────────────────────────────── */
const StatCard: React.FC<{
  icon: React.ReactNode;
  value: React.ReactNode;
  label: string;
  iconBg: string;
  sub?: string;
}> = ({ icon, value, label, iconBg, sub }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-5 flex items-center gap-4">
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${iconBg}`}>
      {icon}
    </div>
    <div>
      <p className="text-2xl font-bold text-slate-900 leading-none">{value}</p>
      <p className="text-sm text-slate-500 mt-0.5">{label}</p>
      {sub && <p className="text-xs text-teal-600 font-medium mt-0.5">{sub}</p>}
    </div>
  </div>
);

/* ─── lesson row ──────────────────────────────────────────────── */
const LessonRow: React.FC<{
  lesson: any;
  index: number;
  onNavigate: (id: string) => void;
}> = ({ lesson, index, onNavigate }) => {
  const videoId = extractYoutubeId(lesson.videoId ?? lesson.videoUrl ?? lesson.youtubeId ?? null);

  return (
    <div
      onClick={() => onNavigate(lesson._id)}
      className="flex items-start gap-4 px-5 py-4 hover:bg-slate-50 transition-colors cursor-pointer group border-b border-slate-100 last:border-0"
    >
      {/* thumbnail */}
      <YoutubeThumbnail videoId={videoId} duration={lesson.duration} size="sm" />

      {/* meta */}
      <div className="flex-1 min-w-0 self-center">
        <div className="flex items-center gap-2 mb-1">
          {/* lesson number badge */}
          <span className="shrink-0 w-5 h-5 rounded-full bg-slate-100 text-slate-500 text-[10px] font-bold flex items-center justify-center group-hover:bg-teal-50 group-hover:text-teal-600 transition-colors">
            {lesson.completed ? (
              <CheckCircle size={13} className="text-teal-500" />
            ) : (
              index + 1
            )}
          </span>
          <h5 className="font-medium text-slate-900 group-hover:text-teal-600 transition-colors truncate">
            {lesson.title}
          </h5>
        </div>

        {lesson.description && (
          <p
            className="text-xs text-slate-500 line-clamp-2 leading-relaxed mb-1.5"
            dangerouslySetInnerHTML={{ __html: lesson.description }}
          />
        )}

        <div className="flex flex-wrap items-center gap-3 text-xs text-slate-400">
          {lesson.duration && (
            <span className="flex items-center gap-1">
              <Clock size={11} />
              {Math.floor(lesson.duration / 60)} min
            </span>
          )}
          {lesson.hasWorksheet && (
            <span className="flex items-center gap-1 text-amber-500">
              <FileText size={11} />
              Worksheet
            </span>
          )}
          {lesson.completed && (
            <span className="flex items-center gap-1 text-teal-600 font-semibold">
              <CheckCircle size={11} />
              Completed
            </span>
          )}
        </div>
      </div>

      {/* play cta */}
      <button className="shrink-0 self-center p-2 rounded-xl hover:bg-teal-50 transition-colors">
        <PlayCircle size={20} className="text-teal-500 group-hover:text-teal-600" />
      </button>
    </div>
  );
};

/* ─── course card ─────────────────────────────────────────────── */
const CourseCard: React.FC<{
  course: any;
  expanded: boolean;
  onToggle: () => void;
  onNavigate: (id: string) => void;
}> = ({ course, expanded, onToggle, onNavigate }) => {
  const modules: any[] = course.modules || [];
  const totalLessons = modules.reduce((a: number, m: any) => a + (m.lessons || []).length, 0);
  const completedLessons = modules.reduce(
    (a: number, m: any) => a + (m.lessons || []).filter((l: any) => l.completed).length,
    0,
  );
  const pct = totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      {/* header */}
      <div
        className="p-5 cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-xl bg-teal-50 flex items-center justify-center shrink-0">
            <BookOpen size={20} className="text-teal-600" />
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold text-slate-900 leading-snug mb-1">
              {course.title}
            </h3>
            {course.description && (
              <p
                className="text-sm text-slate-500 line-clamp-2 mb-2"
                dangerouslySetInnerHTML={{ __html: course.description }}
              />
            )}
            <div className="flex flex-wrap items-center gap-3 text-xs text-slate-400">
              <span>{modules.length} module{modules.length !== 1 ? "s" : ""}</span>
              <span>·</span>
              <span>{totalLessons} lesson{totalLessons !== 1 ? "s" : ""}</span>
              {pct > 0 && (
                <>
                  <span>·</span>
                  <span className="text-teal-600 font-semibold">{pct}% complete</span>
                </>
              )}
            </div>
          </div>

          <div className="shrink-0 p-1.5 rounded-lg hover:bg-slate-200 transition-colors">
            {expanded ? (
              <ChevronUp size={18} className="text-slate-500" />
            ) : (
              <ChevronDown size={18} className="text-slate-500" />
            )}
          </div>
        </div>

        {/* progress bar */}
        {totalLessons > 0 && (
          <div className="mt-4 flex items-center gap-3">
            <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-teal-500 rounded-full transition-all duration-500"
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="text-xs font-semibold text-slate-400 shrink-0 w-9 text-right">
              {completedLessons}/{totalLessons}
            </span>
          </div>
        )}
      </div>

      {/* expanded modules + lessons */}
      {expanded && (
        <div className="border-t border-slate-100">
          {modules.length === 0 ? (
            <p className="px-5 py-6 text-sm text-slate-400 text-center italic">
              No modules available yet.
            </p>
          ) : (
            modules.map((mod: any, modIdx: number) => {
              const modLessons: any[] = mod.lessons || [];
              const modCompleted = modLessons.filter((l) => l.completed).length;
              return (
                <div key={mod._id} className="border-b border-slate-100 last:border-0">
                  {/* module header */}
                  <div className="px-5 py-3 bg-slate-50 flex items-center gap-3">
                    <span className="w-6 h-6 rounded-lg bg-teal-500 text-white text-[11px] font-bold flex items-center justify-center shrink-0">
                      {modIdx + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-700 truncate">
                        {mod.title}
                      </p>
                      {mod.description && (
                        <p
                          className="text-xs text-slate-400 mt-0.5 line-clamp-1"
                          dangerouslySetInnerHTML={{ __html: mod.description }}
                        />
                      )}
                    </div>
                    <span className="text-xs text-slate-400 shrink-0">
                      {modCompleted}/{modLessons.length}
                    </span>
                  </div>

                  {/* lessons */}
                  {modLessons.length === 0 ? (
                    <p className="px-5 py-4 text-xs text-slate-400 italic text-center">
                      No lessons in this module yet.
                    </p>
                  ) : (
                    modLessons.map((lesson: any, lessonIdx: number) => (
                      <LessonRow
                        key={lesson._id}
                        lesson={lesson}
                        index={lessonIdx}
                        onNavigate={onNavigate}
                      />
                    ))
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

/* ─── main page ───────────────────────────────────────────────── */
export default function Dashboard() {
  const { data: courses = [], isLoading } = useGetStudentCoursesQuery(undefined);
  const navigate = useNavigate();
  const auth = useAppSelector((s) => s.auth);
  const [expandedCourses, setExpandedCourses] = useState<Set<string>>(new Set());

  /* stats */
  const totalLessons = courses.reduce(
    (a: number, c: any) =>
      a + (c.modules || []).reduce((b: number, m: any) => b + (m.lessons || []).length, 0),
    0,
  );
  const completedLessons = courses.reduce(
    (a: number, c: any) =>
      a +
      (c.modules || []).reduce(
        (b: number, m: any) => b + (m.lessons || []).filter((l: any) => l.completed).length,
        0,
      ),
    0,
  );
  const progressPercentage = totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;

  /* first incomplete lesson */
  const getContinue = () => {
    for (const course of courses as any[]) {
      for (const module of course.modules || []) {
        for (const lesson of module.lessons || []) {
          if (!lesson.completed) return { course, module, lesson };
        }
      }
    }
    return null;
  };
  const continueItem = getContinue();

  const toggleCourse = (id: string) =>
    setExpandedCourses((prev) => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });

  /* loading */
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4 py-24">
        <div className="w-10 h-10 rounded-full border-2 border-teal-500 border-t-transparent animate-spin" />
        <p className="text-sm text-slate-500">Loading your dashboard…</p>
      </div>
    );
  }

  return (
    <div className="space-y-7 pb-10">

      {/* ── welcome banner ── */}
      <div className="rounded-2xl bg-gradient-to-r from-[#0f1724] to-[#1b2951] p-6 text-white flex items-center justify-between gap-4 overflow-hidden relative">
        {/* decorative circles */}
        <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-white/5" />
        <div className="absolute -right-2 top-10 w-24 h-24 rounded-full bg-teal-400/10" />

        <div className="relative">
          <h1 className="text-xl md:text-2xl font-extrabold leading-tight mb-1">
            Welcome back{auth.firstName ? `, ${auth.firstName}` : ""} 🚀
          </h1>
          <p className="text-sm text-slate-300">
            {continueItem
              ? "Pick up where you left off."
              : completedLessons > 0
              ? "Great progress! Keep going."
              : "Start your first lesson below."}
          </p>
        </div>

        {progressPercentage > 0 && (
          <div className="relative shrink-0 flex flex-col items-center gap-1">
            <div className="relative w-16 h-16">
              <svg className="rotate-[-90deg] w-16 h-16">
                <circle cx="32" cy="32" r="26" stroke="rgba(255,255,255,0.15)" strokeWidth="5" fill="none" />
                <circle
                  cx="32" cy="32" r="26"
                  stroke="#2dd4bf" strokeWidth="5" fill="none"
                  strokeDasharray={2 * Math.PI * 26}
                  strokeDashoffset={2 * Math.PI * 26 * (1 - progressPercentage / 100)}
                  strokeLinecap="round"
                  style={{ transition: "stroke-dashoffset 1s ease" }}
                />
              </svg>
              <span className="absolute inset-0 flex items-center justify-center text-sm font-bold text-white">
                {progressPercentage}%
              </span>
            </div>
            <span className="text-[10px] text-slate-400 font-medium">Overall</span>
          </div>
        )}
      </div>

      {/* ── stat cards ── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard
          icon={<BookOpen size={22} className="text-sky-600" />}
          iconBg="bg-sky-50"
          value={courses.length}
          label="Enrolled Courses"
        />
        <StatCard
          icon={<CheckCircle size={22} className="text-teal-600" />}
          iconBg="bg-teal-50"
          value={completedLessons}
          label="Lessons Completed"
          sub={totalLessons > 0 ? `of ${totalLessons} total` : undefined}
        />
        <StatCard
          icon={<TrendingUp size={22} className="text-violet-600" />}
          iconBg="bg-violet-50"
          value={`${progressPercentage}%`}
          label="Overall Progress"
        />
      </div>

      {/* ── continue watching ── */}
      {continueItem && (() => {
        const { course, module, lesson } = continueItem;
        const videoId = extractYoutubeId(
          lesson.videoId ?? lesson.videoUrl ?? lesson.youtubeId ?? null,
        );
        return (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h2 className="text-base font-bold text-slate-900">Continue Watching</h2>
                <p className="text-xs text-slate-500 mt-0.5">Pick up where you left off</p>
              </div>
              <span className="text-xs font-semibold text-teal-600 bg-teal-50 border border-teal-100 px-2.5 py-1 rounded-full">
                Up next
              </span>
            </div>

            <div
              onClick={() => navigate(`/dashboard/lesson/${lesson._id}`)}
              className="flex items-start gap-4 p-5 cursor-pointer group hover:bg-slate-50 transition-colors"
            >
              <YoutubeThumbnail videoId={videoId} duration={lesson.duration} size="lg" />

              <div className="flex-1 min-w-0 self-center">
                <p className="text-xs text-teal-600 font-semibold mb-1 truncate">
                  {course.title} · {module.title}
                </p>
                <h3 className="text-base font-bold text-slate-900 mb-1 group-hover:text-teal-600 transition-colors leading-snug">
                  {lesson.title}
                </h3>
                {lesson.description && (
                  <p
                    className="text-sm text-slate-500 line-clamp-2 mb-3"
                    dangerouslySetInnerHTML={{ __html: lesson.description }}
                  />
                )}
                <button
                  className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500 text-white rounded-xl text-sm font-semibold hover:bg-teal-600 transition-colors shadow-sm shadow-teal-200"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/dashboard/lesson/${lesson._id}`);
                  }}
                >
                  <Play size={14} />
                  Continue Watching
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── courses list ── */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-base font-bold text-slate-900">Your Courses</h2>
            <p className="text-xs text-slate-500 mt-0.5">Browse all modules and lessons</p>
          </div>
        </div>

        {courses.length === 0 ? (
          <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-10 text-center">
            <div className="w-14 h-14 bg-slate-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <GraduationCap size={28} className="text-slate-300" />
            </div>
            <h3 className="text-base font-bold text-slate-700 mb-1">No Courses Yet</h3>
            <p className="text-sm text-slate-400 mb-5">
              You haven't enrolled in any courses yet.
            </p>
            <button
              onClick={() => navigate("/")}
              className="px-5 py-2.5 bg-teal-500 text-white rounded-xl text-sm font-semibold hover:bg-teal-600 transition-colors"
            >
              Browse Courses
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {(courses as any[]).map((course) => (
              <CourseCard
                key={course._id}
                course={course}
                expanded={expandedCourses.has(course._id)}
                onToggle={() => toggleCourse(course._id)}
                onNavigate={(id) => navigate(`/dashboard/lesson/${id}`)}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── achievement nudge ── */}
      {completedLessons > 0 && (
        <div className="bg-white rounded-2xl border border-amber-100 p-4 flex items-center gap-4">
          <div className="w-11 h-11 bg-amber-50 rounded-xl flex items-center justify-center shrink-0">
            <Award size={22} className="text-amber-500" />
          </div>
          <div>
            <p className="text-sm font-bold text-slate-900">
              {completedLessons >= totalLessons && totalLessons > 0
                ? "🎉 Course complete!"
                : "Keep going!"}
            </p>
            <p className="text-xs text-slate-500">
              You've completed{" "}
              <span className="font-semibold text-slate-700">{completedLessons}</span>{" "}
              lesson{completedLessons !== 1 ? "s" : ""} so far.
              {totalLessons - completedLessons > 0 &&
                ` ${totalLessons - completedLessons} left to go.`}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}